/**
 * diagramPdf - capture a rendered BPR diagram canvas and save it as a
 * single-page PDF (standard A4 landscape/portrait, sized to fill the page).
 *
 * Client-side by design: the layout only exists in the browser (elkjs runs at
 * render time), so the export captures exactly what the user sees. Both
 * libraries load lazily on first click so the deal page bundle stays lean.
 *
 * Export fidelity:
 * 1. Light palette: the captured subtree gets `.export-theme-light` (see
 *    global.css and bpr-page.css) for the duration of the capture, so PDFs
 *    are paper-friendly regardless of the OS theme.
 * 2. SVG style inlining: html-to-image deep-clones <svg> elements WITHOUT
 *    inlining computed styles on their descendants, so anything styled via
 *    CSS classes (React Flow edge paths + edge labels) loses its fill/stroke
 *    in the clone and falls back to SVG-default black. We temporarily copy
 *    the computed paint/text properties into inline style on the live SVG
 *    elements (a visual no-op) so they survive the clone, then restore.
 * 3. Zoom-normalized capture: captures the node bounds at native 1x resolution
 *    with a high pixelRatio (2.5x) so text is crisp.
 * 4. Standard A4 document framing: scales the diagram proportionally to fill
 *    an A4 Landscape (or Portrait) page, with proportional vector header and
 *    legend so viewers open the PDF at normal readable zoom.
 */

import { getNodesBounds } from '@xyflow/react';

/** Windows-unsafe filename characters, replaced with '-'. */
const FILENAME_UNSAFE = /[\\/:*?"<>|]+/g;

/** Margin (CSS px) around the drawing inside the captured image. */
const CAPTURE_MARGIN = 24;

export interface DiagramCapture {
  /** The `.react-flow__viewport` element to capture. */
  element: HTMLElement;
  /** Image dimensions (CSS px) the clone is rendered at. */
  width: number;
  height: number;
  /** Transform applied to the clone so the graph fills the image at <= 1x. */
  transform: string;
}

/**
 * Compute a zoom-normalized capture of a React Flow canvas: the graph's node
 * bounds rendered into an explicitly sized image at native 1x zoom.
 * Returns null when the canvas has no measurable viewport yet.
 */
export function computeFlowCapture(
  container: HTMLElement,
  nodes: Array<{ position: { x: number; y: number }; width?: number; height?: number }>
): DiagramCapture | null {
  const element = container.querySelector<HTMLElement>('.react-flow__viewport');
  if (!element || nodes.length === 0) return null;
  // getNodesBounds accepts node-ish objects (position + measured/explicit size).
  const bounds = getNodesBounds(nodes as Parameters<typeof getNodesBounds>[0]);
  if (bounds.width <= 0 || bounds.height <= 0) return null;

  const width = Math.round(bounds.width + CAPTURE_MARGIN * 2);
  const height = Math.round(bounds.height + CAPTURE_MARGIN * 2);
  return {
    element,
    width,
    height,
    transform: `translate(${-bounds.x + CAPTURE_MARGIN}px, ${-bounds.y + CAPTURE_MARGIN}px) scale(1)`,
  };
}

/**
 * SVG presentation properties that must survive html-to-image's raw <svg>
 * clone. Copied from computed style into inline style for the capture, then
 * restored. `transform` is deliberately absent: React Flow positions SVG
 * groups via the transform ATTRIBUTE (which cloneNode preserves) and inlining
 * the computed CSS value would double-apply it.
 */
const SVG_INLINE_PROPS = [
  'fill',
  'fill-opacity',
  'stroke',
  'stroke-width',
  'stroke-dasharray',
  'stroke-linecap',
  'stroke-linejoin',
  'stroke-opacity',
  'opacity',
  'font-family',
  'font-size',
  'font-weight',
  'letter-spacing',
  'text-anchor',
  'dominant-baseline',
  'visibility',
  'display',
  'color',
] as const;

/**
 * Copy the computed SVG paint/text properties of every SVG element under
 * `root` into inline style (visual no-op on screen; makes the styles survive
 * html-to-image's style-less SVG clone). Returns a restore closure.
 */
function inlineSvgStyles(root: HTMLElement): () => void {
  const touched: Array<{ el: Element; prior: string | null }> = [];
  const svgs = root instanceof SVGSVGElement ? [root] : Array.from(root.querySelectorAll('svg'));
  for (const svg of svgs) {
    for (const el of [svg, ...Array.from(svg.querySelectorAll('*'))]) {
      if (!(el instanceof SVGElement)) continue;
      touched.push({ el, prior: el.getAttribute('style') });
      const computed = window.getComputedStyle(el);
      for (const prop of SVG_INLINE_PROPS) {
        const value = computed.getPropertyValue(prop);
        if (value) el.style.setProperty(prop, value);
      }
    }
  }
  return () => {
    for (const { el, prior } of touched) {
      if (prior === null) el.removeAttribute('style');
      else el.setAttribute('style', prior);
    }
  };
}

/** One legend entry drawn under the PDF title (vector, not captured pixels). */
export interface PdfLegendItem {
  label: string;
  /** 'node' draws a bordered box swatch; 'line' draws a line sample. */
  swatch: 'node' | 'line';
  /** Stroke color (hex). */
  color: string;
  /** Box fill for 'node' swatches (hex; default white). */
  fill?: string;
  /** Dash pattern (pt) for dashed/dotted samples. */
  dash?: number[];
}

/**
 * The standard BPR diagram legend (node statuses + edge kinds), in the same
 * light palette the capture is forced into (export-theme-light), so the PDF
 * legend matches the drawing it annotates.
 */
export const BPR_DIAGRAM_PDF_LEGEND: PdfLegendItem[] = [
  { label: 'existing', swatch: 'node', color: '#64748B', fill: '#FFFFFF' },
  { label: 'new', swatch: 'node', color: '#2563EB', fill: '#EFF6FF' },
  { label: 'changed', swatch: 'node', color: '#B45309', fill: '#FFFFFF', dash: [2, 2] },
  { label: 'removed', swatch: 'node', color: '#CBD5E1', fill: '#FFFFFF' },
  { label: 'integration', swatch: 'line', color: '#64748B' },
  { label: 'manual handoff', swatch: 'line', color: '#B45309', dash: [3, 2] },
  { label: 'data flow', swatch: 'line', color: '#64748B', dash: [1, 2] },
];

export async function downloadDiagramPdf(
  container: HTMLElement,
  title: string,
  capture?: DiagramCapture | null,
  options?: { legend?: PdfLegendItem[] }
): Promise<void> {
  const [{ toPng }, { jsPDF }] = await Promise.all([import('html-to-image'), import('jspdf')]);

  // Force the light palette for the whole capture (computed styles - ours and
  // html-to-image's - resolve against it), inline the SVG styles, then always
  // restore, even on a failed capture.
  container.classList.add('export-theme-light');
  const restoreSvgStyles = inlineSvgStyles(capture?.element ?? container);
  let dataUrl: string;
  let imageCssWidth: number;
  let imageCssHeight: number;
  try {
    const background = window.getComputedStyle(container).backgroundColor || '#ffffff';
    const filter = (node: Node) =>
      !(node instanceof HTMLElement && node.dataset['pdfExclude'] === 'true');
    if (capture) {
      // Zoom-normalized: render the viewport clone at explicit dimensions with high DPI.
      dataUrl = await toPng(capture.element, {
        pixelRatio: 2.5,
        backgroundColor: background,
        width: capture.width,
        height: capture.height,
        style: {
          width: `${capture.width}px`,
          height: `${capture.height}px`,
          transform: capture.transform,
        },
        filter,
      });
      imageCssWidth = capture.width;
      imageCssHeight = capture.height;
    } else {
      // Fallback: capture the container as-is (non-flow surfaces).
      dataUrl = await toPng(container, {
        pixelRatio: 2.5,
        backgroundColor: background,
        filter,
      });
      const image = new Image();
      await new Promise<void>((resolve, reject) => {
        image.onload = () => resolve();
        image.onerror = () => reject(new Error('diagram capture failed'));
        image.src = dataUrl;
      });
      imageCssWidth = image.width / 2.5;
      imageCssHeight = image.height / 2.5;
    }
  } finally {
    restoreSvgStyles();
    container.classList.remove('export-theme-light');
  }

  // Standard A4 page: landscape for wide diagrams, portrait for tall vertical graphs.
  const isPortrait = imageCssHeight > imageCssWidth * 1.25;
  const pdf = new jsPDF({
    orientation: isPortrait ? 'portrait' : 'landscape',
    unit: 'pt',
    format: 'a4',
    compress: true,
  });

  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 28;
  const legend = options?.legend ?? [];

  // Header: Title (15pt bold)
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(15);
  pdf.setTextColor('#0f172a');
  pdf.text(title, margin, margin + 10);

  // Vector legend under the title
  let topOffset = margin + 18;
  if (legend.length > 0) {
    topOffset = margin + 34;
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8.5);
    pdf.setTextColor('#475569');
    let lx = margin;
    const midY = margin + 24;
    for (const item of legend) {
      pdf.setDrawColor(item.color);
      pdf.setLineDashPattern(item.dash ?? [], 0);
      if (item.swatch === 'node') {
        pdf.setFillColor(item.fill ?? '#FFFFFF');
        pdf.roundedRect(lx, midY - 6, 11, 8, 1.5, 1.5, 'FD');
        lx += 15;
      } else {
        pdf.setLineWidth(1.4);
        pdf.line(lx, midY - 2, lx + 14, midY - 2);
        lx += 18;
      }
      pdf.setLineDashPattern([], 0);
      pdf.text(item.label, lx, midY + 1);
      lx += pdf.getTextWidth(item.label) + 12;
    }
    pdf.setTextColor('#000000');
  }

  // Proportional fit: scale diagram image to fill the available page area
  const contentWidth = pageWidth - margin * 2;
  const contentHeight = pageHeight - topOffset - margin;
  const scale = Math.min(contentWidth / imageCssWidth, contentHeight / imageCssHeight);
  const renderWidth = imageCssWidth * scale;
  const renderHeight = imageCssHeight * scale;

  // Center horizontally and vertically within the printable content area
  const x = margin + (contentWidth - renderWidth) / 2;
  const y = topOffset + (contentHeight - renderHeight) / 2;

  pdf.addImage(dataUrl, 'PNG', x, y, renderWidth, renderHeight, undefined, 'FAST');
  pdf.save(`${title.replace(FILENAME_UNSAFE, '-').trim().slice(0, 120) || 'diagram'}.pdf`);
}
